import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Instagram, Linkedin, Youtube, MapPin, Mail, Menu, X } from "lucide-react";

const NAV_LINKS = [
    { to: "/creators", label: "For Creators", tid: "nav-creators" },
    { to: "/team", label: "Team", tid: "nav-team" },
    { to: "/health-audit", label: "Free Audit", tid: "nav-audit" },
    { to: "/pricing", label: "Pricing", tid: "nav-pricing" },
];

export const Navbar = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);

    return (
        <nav className="sticky top-0 z-50 bg-yc-cream/85 backdrop-blur-xl border-b-2 border-yc-ink">
            <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
                <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 font-heading font-black text-xl sm:text-2xl tracking-tighter">
                    <span className="inline-block w-3 h-3 rounded-full bg-yc-pink border-2 border-yc-ink" />
                    CURATED CHAOS
                </Link>
                <div className="hidden md:flex items-center gap-8 font-medium">
                    {NAV_LINKS.map((l) => (
                        <Link key={l.to} to={l.to} data-testid={l.tid} className="hover:text-yc-blue transition-colors">{l.label}</Link>
                    ))}
                </div>
                <div className="flex items-center gap-3">
                    {!user ? (
                        <button data-testid="nav-login-btn" onClick={() => navigate("/login")} className="yc-btn-secondary text-sm py-2 px-4">Log in</button>
                    ) : (
                        <>
                            <button data-testid="nav-dashboard-btn" onClick={() => navigate(user.role === "unset" ? "/role" : `/${user.role}/dashboard`)} className="yc-btn-primary text-sm py-2 px-4">Dashboard</button>
                            <button data-testid="nav-logout-btn" onClick={logout} className="hidden sm:inline-flex yc-btn-secondary text-sm py-2 px-4">Logout</button>
                        </>
                    )}
                    <button data-testid="nav-mobile-toggle" onClick={() => setOpen((v) => !v)} className="md:hidden p-2 rounded-xl border-2 border-yc-ink bg-white" aria-label="Menu">
                        {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                    </button>
                </div>
            </div>
            {open && (
                <div className="md:hidden border-t-2 border-yc-ink bg-yc-cream px-6 py-4 space-y-1" data-testid="nav-mobile-menu">
                    {NAV_LINKS.map((l) => (
                        <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="block py-2 font-medium hover:text-yc-blue">{l.label}</Link>
                    ))}
                    {user && <button onClick={() => { setOpen(false); logout(); }} className="block py-2 font-medium text-yc-pink sm:hidden">Logout</button>}
                </div>
            )}
        </nav>
    );
};

export const Footer = () => (
    <footer className="border-t-2 border-yc-ink mt-24 bg-yc-cream">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 grid grid-cols-1 md:grid-cols-5 gap-8">
            <div className="md:col-span-2">
                <div className="font-heading font-black text-2xl tracking-tighter">CURATED CHAOS</div>
                <p className="text-sm mt-3 text-yc-ink/70 max-w-xs">A vetted creator network. Brands buy confidence.</p>
                <div className="mt-6 space-y-2 text-sm text-yc-ink/90">
                    <div className="flex items-start gap-2" data-testid="footer-address">
                        <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-yc-blue" />
                        <span>We work remotely</span>
                    </div>
                    <a href="mailto:info@curated-chaos.co.uk" className="flex items-center gap-2 hover:text-yc-blue underline underline-offset-4" data-testid="footer-email">
                        <Mail className="w-4 h-4 text-yc-blue" />
                        info@curated-chaos.co.uk
                    </a>
                </div>
                <div className="mt-6 flex items-center gap-3" data-testid="footer-socials">
                    <a href="https://www.instagram.com/wearecuratedchaos" target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="footer-instagram" className="w-9 h-9 rounded-full border-2 border-yc-ink flex items-center justify-center hover:bg-yc-ink hover:text-white transition-colors">
                        <Instagram className="w-4 h-4" />
                    </a>
                    <a href="https://www.tiktok.com/@wearecuratedchaos" target="_blank" rel="noopener noreferrer" aria-label="TikTok" data-testid="footer-tiktok" className="w-9 h-9 rounded-full border-2 border-yc-ink flex items-center justify-center hover:bg-yc-ink hover:text-white transition-colors">
                        <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.66a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.09z"/></svg>
                    </a>
                    <a href="https://www.youtube.com/@Wearecuratedchaos" target="_blank" rel="noopener noreferrer" aria-label="YouTube" data-testid="footer-youtube" className="w-9 h-9 rounded-full border-2 border-yc-ink flex items-center justify-center hover:bg-yc-ink hover:text-white transition-colors">
                        <Youtube className="w-4 h-4" />
                    </a>
                    <a href="https://www.linkedin.com/company/curated-chaos-uk" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" data-testid="footer-linkedin" className="w-9 h-9 rounded-full border-2 border-yc-ink flex items-center justify-center hover:bg-yc-ink hover:text-white transition-colors">
                        <Linkedin className="w-4 h-4" />
                    </a>
                </div>
            </div>
            <div>
                <div className="yc-overline mb-3">Brands</div>
                <Link to="/brands/join" className="block text-sm py-1 hover:text-yc-blue">Join the network</Link>
                <Link to="/health-audit" className="block text-sm py-1 hover:text-yc-blue">Free Health Score audit</Link>
            </div>
            <div>
                <div className="yc-overline mb-3">Creators</div>
                <Link to="/creators" className="block text-sm py-1 hover:text-yc-blue">Apply as a creator</Link>
                <Link to="/how-we-vet" data-testid="footer-how-we-vet" className="block text-sm py-1 hover:text-yc-blue">How we vet</Link>
            </div>
            <div>
                <div className="yc-overline mb-3">Company</div>
                <Link to="/team" data-testid="footer-team" className="block text-sm py-1 hover:text-yc-blue">Team</Link>
                <Link to="/pricing" className="block text-sm py-1 hover:text-yc-blue">Pricing</Link>
            </div>
        </div>
        <div className="border-t-2 border-yc-ink py-4 text-center text-xs text-yc-ink/70 space-y-2">
            <div className="flex flex-wrap items-center justify-center gap-4">
                <Link to="/terms" data-testid="footer-terms" className="hover:text-yc-blue">Terms</Link>
                <Link to="/privacy" data-testid="footer-privacy" className="hover:text-yc-blue">Privacy</Link>
                <Link to="/cookies" data-testid="footer-cookies" className="hover:text-yc-blue">Cookies</Link>
            </div>
            <div>© 2026 by Curated Chaos. All rights reserved copyrighted and trademarked.</div>
        </div>
    </footer>
);
